// Module Import
//---------------------------------
import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import CloseIcon from '../../atoms/icons/CloseIcon'
import ButtonAtom from '../../atoms/buttons/buttons'
import { brandDarkGrey, brandOrange, brandWhite } from '../../../assets/consts/color'
import { secondaryFont } from '../../../assets/consts/fonts'

// Component Import
//---------------------------------

// Styled Component Declarations
//---------------------------------

const ModalContainer = styled.article`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  z-index: 999;

  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  display: ${(props) => (props.opened ? 'flex;' : 'none;')};

  user-select: ${(props) => (!props?.loading ? 'auto;' : 'none;')};

  touch-action: ${(props) => (!props?.loading ? 'auto;' : 'none;')};
`

const ModalHeader = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;

  h3,
  h4 {
    margin: 0;
  }

  h3 {
    color: ${brandOrange};
  }
`

const ModalContentContainer = styled.div`
  font-family: ${secondaryFont};
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0px;
  line-height: 120%;
  color: ${brandDarkGrey};

  padding: 48px;
  background-color: ${brandWhite};
  position: relative;
  width: 40%;
  max-width: 700px;
  border-radius: 8px;
  box-shadow: 0 16px 24px 0 rgba(0, 0, 0, 0.2);

  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px;
  align-items: stretch;
  max-height: 90dvh;
  min-width: 400px;

  @media (max-width: 768px) {
    width: 90%;
    gap: 14px;
    min-width: 250px;
    padding: 48px 24px;
  }

  @media (min-width: 769px) and (max-width: 1450px) {
    width: 70%;
  }
`

const ModalBodyContainer = styled.div`
  //padding: 1rem 1rem;
  font-size: 24px;
  font-weight: normal;

  scrollbar-width: thin;
  overflow: auto;
`

const ModalFooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  a {
    text-decoration: none;
  }
`

const ModalFooterContainerInner = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  //padding: 10px;

  button {
    //width: 121px;
  }
`

const ModalTitle = styled.div`
  font-family: ${secondaryFont};
  font-size: 23px;
  font-weight: 400;
  letter-spacing: 0px;
  line-height: 120%;
  color: ${brandOrange};
  margin: 0;

  @media only screen and (min-width: 48em) {
    font-size: 28px;
  }
`

const ModalSubTitle = styled.div`
  font-family: ${secondaryFont};
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 0px;
  line-height: 120%;
  margin: 0;
`

const ModalCloseIcon = styled(CloseIcon)`
  cursor: pointer;

  position: absolute;
  top: 5px;
  right: 5px;

  path {
    fill: ${brandDarkGrey};
  }
`

// Component Export
//---------------------------------

export default function Modal({
  title,
  subTitle,
  onClose,
  isOpen,
  children,
  onSubmit,
  onSecondaryButton,
  isLoading,
  primaryButtonText = 'Ok',
  secondaryButtonText = 'Cancel',
  disableButtons = false,
  selector = '#modal-root',
  showCallButton = false,
  phoneNumber,
}) {
  const [mounted, setMounted] = useState(false)
  const [container, setContainer] = useState(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden'
    } else {
      document.body.style.overflowY = 'auto'
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose()
    }
  }, [isLoading, onClose])

  useEffect(() => {
    const el = document.querySelector(selector)
    if (el) {
      setContainer(el)
      setMounted(true)
    }
  }, [selector])

  if (!mounted || !container) return null

  return createPortal(
    <ModalContainer opened={isOpen} loading={isLoading}>
      <ModalContentContainer>
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          {subTitle && <ModalSubTitle>{subTitle}</ModalSubTitle>}
          <ModalCloseIcon className="close-icon" onClick={handleClose} />
        </ModalHeader>
        <ModalBodyContainer>{children}</ModalBodyContainer>
        {!disableButtons && (
          <ModalFooterContainer>
            <ModalFooterContainerInner>
              <ButtonAtom
                buttonType="Primary Button"
                buttonCopy={primaryButtonText}
                isLoading={isLoading}
                disabled={isLoading}
                onClick={onSubmit}
              />
              <ButtonAtom
                buttonType="Secondary Button Without Arrow"
                buttonCopy={secondaryButtonText}
                disabled={isLoading}
                onClick={onSecondaryButton || handleClose}
              />
            </ModalFooterContainerInner>

            {showCallButton && phoneNumber && (
              <a href={`tel:${phoneNumber}`} aria-disabled={isLoading}>
                <ButtonAtom
                  buttonType="Secondary Button Without Arrow"
                  buttonCopy={'Call Us'}
                  disabled={isLoading}
                />
              </a>
            )}
          </ModalFooterContainer>
        )}
      </ModalContentContainer>
    </ModalContainer>,
    container
  )
}

Modal.propTypes = {}
